// Game-config editor — singleton, not a list. Two pools (HP + mana), each
// with base / baseGain / perStat. Publish writes /content/game-config.json
// and broadcasts a catalogueUpdate so the next level-up applies the new
// formula. Existing characters' already-banked maxHpBase / maxManaBase do
// NOT change retroactively; the new gain kicks in at their next level-up.

import type { GameConfig } from "@vinyl/shared";
import { el } from "./dom.js";

export interface ConfigScreenCallbacks {
  onDirtyChange: (dirty: boolean) => void;
}

export interface ConfigScreen {
  root: HTMLElement;
  setConfig(config: GameConfig): void;
  getDraft(): GameConfig;
  isDirty(): boolean;
  markPublished(): void;
}

interface Draft extends GameConfig {
  __isDirty?: boolean;
}

function blank(): Draft {
  return {
    hp: { base: 50, baseGain: 0, perStat: 5 },
    mana: { base: 20, baseGain: 0, perStat: 5 },
  };
}

export function createConfigScreen(cb: ConfigScreenCallbacks): ConfigScreen {
  let draft: Draft = blank();

  const body = el("div", { class: "editor config-screen" });
  const root = el("div", { class: "items-layout config-layout" }, body);

  function markDirty(): void {
    draft.__isDirty = true;
    cb.onDirtyChange(true);
  }

  function render(): void {
    body.replaceChildren();
    body.append(
      el("h2", {}, "Game config"),
      el(
        "p",
        { class: "config-hint" },
        "Tunes the per-level-up HP and mana gain. Pool size at character creation = base + (one level-up gain). Each subsequent level-up adds (baseGain + perStat × driving stat). CON drives HP; max(INT, FAI) drives mana. Existing characters keep their banked pool — only their next level-up uses the new formula.",
      ),
      section("HP pool (CON-driven)", "hp"),
      section("Mana pool (max(INT, FAI)-driven)", "mana"),
    );
  }

  function section(title: string, key: "hp" | "mana"): HTMLElement {
    const pool = draft[key];
    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, title),
      el(
        "div",
        { class: "editor-grid" },
        numberField(`Base (creation only)`, pool.base, (v) => {
          draft[key] = { ...draft[key], base: Math.max(0, Math.floor(v)) };
          markDirty();
        }),
        numberField(`Base gain per level`, pool.baseGain, (v) => {
          draft[key] = { ...draft[key], baseGain: Math.max(0, Math.floor(v)) };
          markDirty();
        }),
        numberField(`Per stat per level`, pool.perStat, (v) => {
          draft[key] = { ...draft[key], perStat: Math.max(0, Math.floor(v)) };
          markDirty();
        }),
      ),
      el(
        "div",
        { class: "config-preview" },
        "Preview at CON/INT 10 → level 1 pool: ",
        el(
          "b",
          {},
          String(pool.base + pool.baseGain + pool.perStat * 10),
        ),
        " · gain per level after: ",
        el("b", {}, String(pool.baseGain + pool.perStat * 10)),
      ),
    );
  }

  return {
    root,
    setConfig(c) {
      draft = {
        hp: { ...c.hp },
        mana: { ...c.mana },
        __isDirty: false,
      };
      cb.onDirtyChange(false);
      render();
    },
    getDraft() {
      return { hp: { ...draft.hp }, mana: { ...draft.mana } };
    },
    isDirty() {
      return !!draft.__isDirty;
    },
    markPublished() {
      delete draft.__isDirty;
      cb.onDirtyChange(false);
      render();
    },
  };

  function numberField(
    label: string,
    value: number,
    onChange: (v: number) => void,
  ): HTMLElement {
    const input = el("input", {
      type: "number",
      value: String(value),
      step: "1",
      min: "0",
      oninput: (e: Event) => {
        const v = Number.parseInt((e.target as HTMLInputElement).value, 10);
        if (Number.isFinite(v)) onChange(v);
      },
    });
    return el("div", { class: "field" }, el("label", {}, label), input);
  }
}
